import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { connect } from "react-redux";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

import * as actions from "../../store/actions/index";
import {
  formatDateToTime,
  formatDateWithTime,
  withRouter,
} from "../../shared/utility";
import { CampaignProductsDistributedViewModel } from "../../viewmodels/campaigns/CampaignProductsDistributedViewMode";
import EmptyModal from "../../components/modals/EmptyModal";

function CampaignProductsDistributed(props) {
  const { logout, addSnackbar } = props;
  const { campaignId } = useParams();
  const navigate = useNavigate();

  const {
    addDistributedProduct,
    campaignInfo,
    campaignTypesCount,
    deleteDistributedProduct,
    campaignVisits,
    emptyModal,
    getCampaignProductsDistributed,
    loading,
    productsSold,
    samplesDistributed,
    setEmptyModelOpen,
    endCampaign,
  } = CampaignProductsDistributedViewModel(logout, campaignId, addSnackbar);

  useEffect(() => {
    getCampaignProductsDistributed(campaignId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const handleAddRecord = (type) => {
    addDistributedProduct(parseInt(campaignId), type);
  };

  const typeBodyTemplate = (rowData, typeName) => {
    return <span className="text-sm text-gray-900">{typeName}</span>;
  };

  const timeBodyTemplate = (rowData) => {
    return (
      <>
        <span className="text-sm text-gray-900 lg:hidden">
          {formatDateToTime(rowData.created_at)}
        </span>
        <span className="text-sm text-gray-900 hidden lg:inline">
          {formatDateWithTime(rowData.created_at)}
        </span>
      </>
    );
  };

  const actionBodyTemplate = (rowData, type) => {
    return (
      <button
        type="button"
        className="text-red-600 hover:text-red-800 text-sm font-medium"
        onClick={() => deleteDistributedProduct(rowData.id, type)}
      >
        Delete
      </button>
    );
  };

  return (
    <div className="bg-gray-50">
      <main>
        <div className="p-3">
          <div className="mb-6 px-4 sm:px-6 lg:px-8">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">
                    Campaign Details
                  </h2>
                  {campaignInfo && (
                    <div className="space-y-2">
                      <p className="text-lg font-bold text-gray-800">
                        <span className="font-bold">Store Name:</span>{" "}
                        {campaignInfo.name}
                      </p>
                      <p className="text-lg font-bold text-gray-800">
                        <span className="font-bold">Address:</span>{" "}
                        {campaignInfo.address}
                      </p>
                      <p className="text-lg font-bold text-gray-800">
                        <span className="font-bold">Employee:</span>{" "}
                        {campaignInfo.employee_name}
                      </p>
                      <p className="text-lg font-bold text-gray-800">
                        <span className="font-bold">Product:</span>{" "}
                        {campaignInfo.product.name}
                      </p>
                    </div>
                  )}
                </div>
                <div>
                  {campaignInfo && campaignInfo.active === 1 && (
                    <div>
                      <p className="text-lg font-bold text-gray-800">
                        <span className="font-bold">Start Date:</span>{" "}
                        {formatDateWithTime(campaignInfo.campaign_start)}
                      </p>
                      {campaignInfo.campaign_end ? (
                        <p className="text-lg font-bold text-gray-800">
                          <span className="font-bold">End Date:</span>{" "}
                          {formatDateWithTime(campaignInfo.campaign_end)}
                        </p>
                      ) : (
                        <p className="text-lg font-bold text-gray-800">
                          <span className="font-bold">End Date:</span>{" "}
                          <span className="text-green-600">ACTIVE</span>
                        </p>
                      )}
                      <button
                        type="button"
                        className="px-4 mt-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-semibold shadow-sm"
                        onClick={() => endCampaign(campaignId)}
                      >
                        End Campaign
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-3 gap-6">
              {/* Visits Column */}
              <div className="flex flex-col">
                <div className="text-center mb-2">
                  <span className="text-3xl font-bold text-blue-600">
                    {campaignTypesCount["0"]?.count ?? 0}
                  </span>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Total Visits
                  </p>
                </div>
                <button
                  onClick={() => handleAddRecord(0)}
                  className="w-full px-6 py-4 lg:px-4 lg:py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 font-semibold shadow-sm mb-4 text-base lg:text-sm"
                >
                  Add Visit
                </button>
                <div className="overflow-hidden card-custom flex-1">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Campaign Visits
                    </h3>
                  </div>
                  {loading ? (
                    <div className="px-4 py-8 text-center text-gray-500 text-sm">
                      Loading...
                    </div>
                  ) : !campaignVisits || campaignVisits.length === 0 ? (
                    <div className="px-4 py-8 text-center text-gray-500 text-sm">
                      No visits recorded
                    </div>
                  ) : (
                    <DataTable
                      value={campaignVisits}
                      dataKey="id"
                      tableClassName="min-w-full bg-white text-sm"
                      className="border-none"
                      stripedRows
                    >
                      <Column
                        body={timeBodyTemplate}
                        header="Time"
                        headerStyle={{ width: "50%" }}
                      />
                      <Column
                        body={(rowData) => actionBodyTemplate(rowData, 0)}
                        header="Action"
                        headerStyle={{ width: "20%", textAlign: "right" }}
                        bodyStyle={{ textAlign: "right" }}
                      />
                    </DataTable>
                  )}
                </div>
              </div>

              {/* Samples Distributed Column */}
              <div className="flex flex-col">
                <div className="text-center mb-2">
                  <span className="text-3xl font-bold text-orange-600">
                    {campaignTypesCount["1"]?.count ?? 0}
                  </span>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Total Samples
                  </p>
                </div>
                <button
                  onClick={() => handleAddRecord(1)}
                  className="w-full px-6 py-4 lg:px-4 lg:py-3 bg-orange-500 text-white rounded-md hover:bg-orange-600 font-semibold shadow-sm mb-4 text-base lg:text-sm"
                >
                  Add Sample
                </button>
                <div className="overflow-hidden card-custom flex-1">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Samples Distributed
                    </h3>
                  </div>
                  {loading ? (
                    <div className="px-4 py-8 text-center text-gray-500 text-sm">
                      Loading...
                    </div>
                  ) : !samplesDistributed || samplesDistributed.length === 0 ? (
                    <div className="px-4 py-8 text-center text-gray-500 text-sm">
                      No samples distributed
                    </div>
                  ) : (
                    <DataTable
                      value={samplesDistributed}
                      dataKey="id"
                      tableClassName="min-w-full bg-white text-sm"
                      className="border-none"
                      stripedRows
                    >
                      <Column
                        body={timeBodyTemplate}
                        header="Time"
                        headerStyle={{ width: "50%" }}
                      />
                      <Column
                        body={(rowData) => actionBodyTemplate(rowData, 1)}
                        header="Action"
                        headerStyle={{ width: "20%", textAlign: "right" }}
                        bodyStyle={{ textAlign: "right" }}
                      />
                    </DataTable>
                  )}
                </div>
              </div>

              {/* Products Sold Column */}
              <div className="flex flex-col">
                <div className="text-center mb-2">
                  <span className="text-3xl font-bold text-green-600">
                    {campaignTypesCount["2"]?.count ?? 0}
                  </span>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Total Sales
                  </p>
                </div>
                <button
                  onClick={() => handleAddRecord(2)}
                  className="w-full px-6 py-4 lg:px-4 lg:py-3 bg-green-500 text-white rounded-md hover:bg-green-600 font-semibold shadow-sm mb-4 text-base lg:text-sm"
                >
                  Add Sale
                </button>
                <div className="overflow-hidden card-custom flex-1">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Products Sold
                    </h3>
                  </div>
                  {loading ? (
                    <div className="px-4 py-8 text-center text-gray-500 text-sm">
                      Loading...
                    </div>
                  ) : !productsSold || productsSold.length === 0 ? (
                    <div className="px-4 py-8 text-center text-gray-500 text-sm">
                      No products sold
                    </div>
                  ) : (
                    <DataTable
                      value={productsSold}
                      dataKey="id"
                      tableClassName="min-w-full bg-white text-sm"
                      className="border-none"
                      stripedRows
                    >
                      <Column
                        body={timeBodyTemplate}
                        header="Time"
                        headerStyle={{ width: "50%" }}
                      />
                      <Column
                        body={(rowData) => actionBodyTemplate(rowData, 2)}
                        header="Action"
                        headerStyle={{ width: "20%", textAlign: "right" }}
                        bodyStyle={{ textAlign: "right" }}
                      />
                    </DataTable>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
    </div>
  );
}

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch(actions.logout()),
  addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(CampaignProductsDistributed),
);
